// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { systemBuilderYourSystemViewStyles } from "@plumberssupply/App/Components/SystemBuilder/SystemBuilderYourSystemView";
import IconList from "@plumberssupply/XDL/Icons/IconList";
import { color, createLinkIconStyles, xScreen } from "@plumberssupply/XDL/Utilities";
import { css } from "styled-components";

systemBuilderYourSystemViewStyles.systemRank = {
    variant: "h3",
    forwardAs: "p",
    css: css`
        margin-bottom: 0;
        padding: 8px 12px;
        width: 100%;
        color: ${color("common.white")};
        background: ${color("primary.main")};
        &.good {
            background: ${color("common.ui11")};
        }
        &.better {
            background: #00426b;
        }
        &.best {
            background: ${color("primary.main")};
        }
    `,
};
systemBuilderYourSystemViewStyles.specifications = {
    css: css`
        display: flex;
        column-gap: 8px;
        flex-wrap: wrap;
    `,
};
systemBuilderYourSystemViewStyles.specificationsWrapper = {
    css: css`
        padding: 12px 12px 16px;
    `,
};
systemBuilderYourSystemViewStyles.sectionTitle = {
    variant: "h5",
    css: css`
        margin-bottom: 8px;
        width: 100%;
    `,
};
systemBuilderYourSystemViewStyles.note = {
    size: 12,
    lineHeight: "14px",
    css: css`
        margin-top: 6px;
        width: 100%;
        display: flex;
    `,
};
systemBuilderYourSystemViewStyles.systemWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        border: 1px solid ${color("common.ui7")};
        width: calc((100% - 44px) / 3);
        min-width: calc((100% - 44px) / 3);
        ${xScreen("lte", "md")} {
            width: 352px;
            min-width: 352px;
        }

        ${xScreen("lte", "sm")} {
            width: 326px;
            min-width: 326px;
        }

        ${({ maxNumber }: any) =>
            maxNumber &&
            maxNumber > 0 &&
            css`
                .specifications {
                    min-height: calc((${maxNumber}*25px));
                }
            `}
    `,
};
systemBuilderYourSystemViewStyles.container = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        gap: 22px;
        justify-content: center;
        flex-direction: row-reverse;

        &.otherSystems {
            flex-direction: row;
            justify-content: flex-start;
        }

        ${xScreen("lte", "md")} {
            flex-direction: row;
            gap: 24px 16px;
            &.threeRanked {
                justify-content: flex-start;
            }
            &.otherSystems {
                justify-content: space-between;
            }
        }
    `,
};
systemBuilderYourSystemViewStyles.overlayStyles = {
    css: css`
        min-height: 40vh;
    `,
};
systemBuilderYourSystemViewStyles.containerWrapper = {
    css: css`
        padding-bottom: 22px;
        ${xScreen("lte", "md")} {
            max-width: 768px;
            margin: 0 auto;
            padding-bottom: 24px;
        }
        ${xScreen("lte", "sm")} {
            max-width: 360px;
        }
    `,
};
systemBuilderYourSystemViewStyles.headingAndTextStyles = {
    text: {
        size: 12,
        lineHeight: "14px",
        css: css`
            width: auto;
        `,
    },
    heading: {
        weight: "bold",
        size: 12,
        lineHeight: "14px",
        css: css`
            text-transform: capitalize;
            &:after {
                content: ":";
            }
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            column-gap: 4px;
            width: calc(50% - 4px);
            margin-bottom: 6px;
            padding-bottom: 4px;
            box-shadow: 0 1px 0 0 ${color("common.ui8")};
        `,
    },
};
systemBuilderYourSystemViewStyles.totalSection = {
    css: css`
        background: ${color("common.ui12")};
        padding: 12px;
        margin-top: auto;
    `,
};

systemBuilderYourSystemViewStyles.totalHeadingAndText = {
    text: {
        variant: "h5",
        forwardAs: "p",
        css: css`
            width: auto;
            margin-bottom: 0;
        `,
    },
    heading: {
        variant: "h6",
        forwardAs: "p",
        css: css`
            text-transform: capitalize;
            margin-bottom: 0;

            &:after {
                content: ":";
            }
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            column-gap: 8px;
            margin-bottom: 8px;
            align-items: center;
        `,
    },
};
systemBuilderYourSystemViewStyles.addToListWrappper = {
    css: css`
        display: flex;
        justify-content: flex-end;
        margin-top: 12px;
    `,
};
systemBuilderYourSystemViewStyles.addToList = {
    ...createLinkIconStyles(
        IconList,
        css`
            line-height: 14px;
            font-size: 12px;
        `,
        css`
            &&&& {
                opacity: 1;
                width: 12px;
                > svg {
                    width: 12px;
                    height: 13px;
                }
                color: ${color("secondary", 0.5)};
            }
        `,
    ),
};
systemBuilderYourSystemViewStyles.addToCartButton = {
    css: css`
        width: 100%;
    `,
};
systemBuilderYourSystemViewStyles.components = {
    css: css`
        padding-inline: 12px;
    `,
};
systemBuilderYourSystemViewStyles.loadingSpinner = {
    size: 15,
    css: css`
        margin-top: auto;
        margin-bottom: auto;
        margin-left: auto;
    `,
};

systemBuilderYourSystemViewStyles.separator = {
    css: css`
        border-top: 1px solid ${color("common.ui7")};
        width: 100%;
        margin-block: 32px;
    `,
};
